import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { ArrowLeft, ArrowRight, Calendar, Clock, Loader2 } from 'lucide-react';
import { assetUrl } from '../../lib/siteAssets';
import { useSeo } from '../../lib/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' }) : '');

const articleLd = (post) => ({
  '@context': 'https://schema.org', '@type': 'BlogPosting', headline: post.title, description: post.excerpt,
  image: post.image ? [assetUrl(post.image)] : undefined, datePublished: post.date, articleSection: post.category,
  author: { '@type': 'Organization', name: 'The Expat Studio' }, publisher: { '@type': 'Organization', name: 'The Expat Studio', logo: { '@type': 'ImageObject', url: `${window.location.origin}/favicon-512.png` } },
  mainEntityOfPage: window.location.href,
});

export const BlogPostView = ({ postId }) => {
  const [post, setPost] = useState(undefined);
  useEffect(() => { axios.get(`${API}/blog/${postId}`).then((r) => setPost(r.data)).catch(() => setPost(null)); }, [postId]);
  useSeo(post && { title: post.title, description: post.excerpt, image: post.image ? assetUrl(post.image) : null, type: 'article', jsonLd: articleLd(post) }, [post?.id]);

  if (post === undefined) return <div className="min-h-screen pt-32 flex justify-center"><Loader2 className="animate-spin text-[#3B164C]" size={40} /></div>;
  if (!post) return <div className="min-h-screen pt-32 text-center" data-testid="blog-post-not-found"><p className="text-[#1E1622]/60">This article isn't available.</p><Link to="/blog" className="link-gold mt-4 inline-block">Back to the blog</Link></div>;

  const raw = post.content || post.excerpt || '';
  const isHtml = /<[a-z][\s\S]*>/i.test(raw);
  const paragraphs = isHtml ? [] : raw.split(/\n\s*\n/).filter(Boolean);
  return (
    <article className="min-h-screen pt-28 pb-20" data-testid="blog-post">
      <div className="container-custom max-w-3xl">
        <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 text-sm text-[#3B164C] hover:text-[#C5A059] mb-8"><ArrowLeft size={16} /> All articles</Link>
        <span className="eyebrow mb-4"><span className="w-10 h-px bg-[#C5A059]" /> {post.category}</span>
        <h1 className="font-display text-4xl sm:text-5xl font-medium text-[#1E1622] leading-tight mb-5" data-testid="blog-post-title">{post.title}</h1>
        <div className="flex items-center gap-5 text-sm text-[#1E1622]/55 mb-8">
          <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {fmt(post.date)}</span>
          {post.read_time && <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {post.read_time}</span>}
        </div>
        {post.image && <img src={assetUrl(post.image)} alt={post.title} className="w-full aspect-[16/9] object-cover rounded-3xl mb-10" />}
        {isHtml ? (
          <div className="article-body text-lg" data-testid="blog-post-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(raw, { ADD_ATTR: ['target'] }) }} />
        ) : (
          <div className="space-y-5 text-lg leading-relaxed text-[#1E1622]/80 font-light" data-testid="blog-post-content">
            {paragraphs.map((t, i) => <p key={i} className="whitespace-pre-line">{t}</p>)}
          </div>
        )}
        <div className="mt-14 p-8 rounded-3xl bg-[#2D1037] text-[#FAF7F2]">
          <p className="font-display text-2xl mb-2">Planning your move?</p>
          <p className="text-[#FAF7F2]/70 mb-6 font-light">Our relocation guides walk you through every step, from visas to your first week in your new home.</p>
          <Link to="/shop" data-testid="blog-post-shop-cta" className="btn-gold inline-flex items-center gap-2">Browse the guides <ArrowRight size={16} /></Link>
        </div>
      </div>
    </article>
  );
};
